/**
 * #5237: the short "+ tax and fees" note on the public class and private-lesson pages, where a
 * signed-out visitor sees the price before any checkout.
 *
 * The fee words come from `computeFeeDisplayLines` — the rule checkout's fee rows use — run on a
 * sample amount for a card and for a bank payment, so the note names what those rows will say:
 * the gym's bundle label, the one fee a parent pays, or "fees" when there are several.
 */

import {
  getParentAchFee,
  getParentStripeFee,
  getParentTechFee,
  type FeeDisplaySettings,
  type FeePayerOption,
} from '@/utils/billing/billingConstants';
import { computeFeeDisplayLines } from '@/utils/billing/computeFeeDisplayLines';

/** What one location adds to a listed price at checkout. */
export interface PublicPriceLocationExtras {
  locationName: string | null;
  taxOn: boolean;
  /** The fee words checkout shows there, or null when the parent pays no fee. */
  feeLabel: string | null;
}

const SAMPLE_SUBTOTAL_DOLLARS = 100;
const SEVERAL_FEES = 'fees';

export function publicFeeLabel(input: {
  technologyFeePayer: FeePayerOption;
  stripeFeePayer: FeePayerOption;
  achFeePayer: FeePayerOption;
  display: FeeDisplaySettings;
  surchargeRestricted: boolean;
}): string | null {
  const technologyFee = getParentTechFee(SAMPLE_SUBTOTAL_DOLLARS, input.technologyFeePayer);
  const labels = new Set<string>();
  for (const kind of ['card', 'ach'] as const) {
    const processingFee =
      kind === 'card'
        ? getParentStripeFee(SAMPLE_SUBTOTAL_DOLLARS, input.stripeFeePayer)
        : getParentAchFee(SAMPLE_SUBTOTAL_DOLLARS, input.achFeePayer);
    const { rows } = computeFeeDisplayLines({
      technologyFee,
      processingFee,
      processingFeeKind: kind,
      displaySettings: input.display,
      surchargeRestricted: input.surchargeRestricted,
      stripeFeePayerIsParent: input.stripeFeePayer === 'parent_pays',
      viewerType: 'parent',
    });
    if (rows.length === 1) labels.add(rows[0].label);
    else if (rows.length > 1) labels.add(SEVERAL_FEES);
  }
  if (labels.size === 0) return null;
  return labels.size === 1 ? [...labels][0] : SEVERAL_FEES;
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function extrasPhrase(taxOn: boolean, feeLabel: string | null): string | null {
  if (taxOn && feeLabel) return `+ tax and ${feeLabel}`;
  if (taxOn) return '+ tax';
  if (feeLabel) return `+ ${feeLabel}`;
  return null;
}

/**
 * The note's lines. One location, or several that agree: "+ tax and Admin Fee". Tax at only some
 * of them: the fee line, then "+ tax at <those locations>". Fees that differ by location: one
 * line per location.
 */
export function publicPriceExtrasLines(locations: PublicPriceLocationExtras[]): string[] {
  if (locations.length === 0) return [];
  const feeLabels = new Set(locations.map((loc) => loc.feeLabel));
  if (feeLabels.size > 1) {
    return locations.flatMap((loc) => {
      const phrase = extrasPhrase(loc.taxOn, loc.feeLabel);
      if (!phrase) return [];
      return [loc.locationName ? `${loc.locationName}: ${phrase}` : phrase];
    });
  }

  const feeLabel = locations[0].feeLabel;
  const taxed = locations.filter((loc) => loc.taxOn);
  if (taxed.length === 0 || taxed.length === locations.length) {
    const phrase = extrasPhrase(taxed.length > 0, feeLabel);
    return phrase ? [phrase] : [];
  }
  const taxedNames = taxed.map((loc) => loc.locationName).filter((name): name is string => !!name);
  const lines = feeLabel ? [`+ ${feeLabel}`] : [];
  lines.push(taxedNames.length === taxed.length ? `+ tax at ${joinNames(taxedNames)}` : '+ tax at some locations');
  return lines;
}
